// src/oura/overview/HubEmpty.tsx
//
// What a business with no scored scan shows, by stage (JOURNEY-SPEC v1.1 §2.2):
// the Overview Next card on top of Overview, then one sentence with one press
// in place of "Run a New Scan from the Businesses table".
//
// Reads the business page's stage through HubContext. Outside it (New scan
// switch off) both render nothing and the caller keeps today's empty state.

import { SearchCheckIcon } from "lucide-react";
import { ArrowLink, PillButton, Surface, TYPE } from "@oura/ui";
import { cn } from "../lib/cn";
import { HubNextCard } from "./HubNextCard";
import type { HubAct, HubStage } from "./hubStage";
import { useHub } from "./useHubStage";

/** The stage could not be read: say so, with a retry, never a journey press built on a failed read (Jack, #4296). */
function HubReadFailed({ onRetry, className }: { onRetry(): void; className?: string }) {
    return (
        <Surface className={cn("items-start gap-3", className)}>
            <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>Couldn't read where this business stands</h2>
            <p role="alert" className={cn(TYPE.tb, "m-0 text-gray-600")}>Its onboarding or plan did not load. Try again.</p>
            <PillButton variant="primary" onClick={onRetry}>Try again</PillButton>
        </Surface>
    );
}

/** The Overview Next card, carried out through the business page. Nothing while the stage is read. */
export function HubNextSlot({ className }: { className?: string }) {
    const hub = useHub();
    const next = hub?.next;
    if (hub && !hub.stage && hub.failed) return <HubReadFailed className={className} onRetry={hub.refresh} />;
    if (!hub || !next) return null;
    return (
        <div className={className}>
            <HubNextCard next={next} onAct={hub.act} busy={hub.busy} label={hub.stage?.kind === "onboarding" ? "First AI check" : "Scan"} />
        </div>
    );
}

interface EmptyCopy {
    title: string;
    line: string;
    press: { label: string; act: HubAct; link: boolean } | null;
}

/** The sentence and the one press a stage gives a screen with nothing scored. */
function emptyCopy(stage: HubStage, name: string): EmptyCopy {
    switch (stage.kind) {
        case "prospect_new":
            return { title: "No scan yet", line: `Draft the 50 to see what AI says about ${name}.`, press: { label: "Draft the 50", act: { kind: "draft_new" }, link: false } };
        case "prospect_draft":
            return { title: "No scan yet", line: `Draft the 50 to see what AI says about ${name}.`, press: { label: "Continue the draft", act: { kind: "continue", draft: stage.draft }, link: false } };
        case "prospect_running":
            return { title: "Scan in progress", line: "These pages fill in when it finishes.", press: null };
        case "onboarding":
            if (stage.checked) return { title: "Not yet scored", line: "There is no scored scan to show yet.", press: null };
            return {
                title: "No AI check yet",
                line: "The first AI check fills these pages. It starts from the onboarding screen.",
                // Only opens the page: the Next card there is pressed by the person, not by this link.
                press: { label: "Open onboarding", act: { kind: "onboarding", press: false }, link: true },
            };
        case "active_unscanned":
            return { title: "No AI check yet", line: `Draft the 50 to run ${name}'s first AI check.`, press: { label: "Draft the 50", act: { kind: "first_check", draftId: null, atStart: false }, link: false } };
        default:
            return { title: "Not yet scored", line: "There is no scored scan to show yet.", press: null };
    }
}

/**
 * The stage's empty state. `withPress` false leaves the press to the Next card
 * above it (Overview), so the same button never shows twice on one screen.
 * While the stage is read, a quiet block of the same size: never a blank page.
 */
export function HubEmptyState({ name, withPress = true }: { name: string; withPress?: boolean }) {
    const hub = useHub();
    const stage = hub?.stage;
    if (!hub) return null;
    // Without the press, the Next card slot above carries the retry: one per screen.
    if (!stage && hub.failed && withPress) return <HubReadFailed onRetry={hub.refresh} />;
    if (!stage) return <div role="status" aria-label="Loading" className="h-[236px] animate-pulse rounded-2xl bg-white/70" />;
    const copy = emptyCopy(stage, name);
    const press = withPress ? copy.press : null;
    // One blue press per screen: the header's stage pill keeps it when it has one.
    const headerHasPrimary = hub.pills.some((p) => p.primary);
    return (
        <Surface className="items-center gap-2 py-12 text-center">
            <SearchCheckIcon aria-hidden size={24} className="text-gray-300" />
            <h2 className={cn(TYPE.tt, "m-0 text-gray-900")}>{copy.title}</h2>
            <p className={cn(TYPE.tb, "m-0 max-w-md text-gray-500")}>{copy.line}</p>
            {press && (press.link ? (
                <ArrowLink className="mt-2" onClick={() => hub.act(press.act)}>{press.label}</ArrowLink>
            ) : (
                <PillButton variant={headerHasPrimary ? "secondary" : "primary"} className="mt-2" onClick={() => hub.act(press.act)}>{press.label}</PillButton>
            ))}
        </Surface>
    );
}
